import { Controller } from "@hotwired/stimulus"

// Structured editor for registry array props (FAQ items, menu links, features,
// footer columns, and nested lists). The canonical value remains JSON in a
// hidden field so the existing block autosave endpoint stays unchanged.
export default class extends Controller {
  static targets = ["list", "input"]
  static values = { schema: String, items: String }

  connect() {
    this.schema = this.parse(this.schemaValue, {})
    this.items = this.parse(this.itemsValue, [])
    this.render()
  }

  add() {
    this.items.push(this.blankItem(this.schema))
    this.syncAndRender()
  }

  render() {
    this.listTarget.replaceChildren()
    this.items.forEach((item, index) => this.listTarget.append(this.itemCard(item, this.schema, index)))
  }

  itemCard(item, schema, index) {
    const card = this.el("div", "rounded-lg border border-slate-200 dark:border-slate-600 p-2 space-y-2")
    const head = this.el("div", "flex items-center justify-between")
    head.append(this.el("span", "text-[10px] font-semibold uppercase tracking-wider text-slate-400", `Item ${index + 1}`))

    const actions = this.el("div", "flex gap-1")
    actions.append(this.actionButton("↑", () => this.move(index, -1), index === 0))
    actions.append(this.actionButton("↓", () => this.move(index, 1), index === this.items.length - 1))
    actions.append(this.actionButton("Remove", () => this.remove(index), false, "text-red-500"))
    head.append(actions)
    card.append(head)

    Object.entries(schema).forEach(([key, spec]) => {
      const field = this.el("label", "block space-y-1")
      field.append(this.el("span", "block text-[10px] text-slate-500 dark:text-slate-400", this.labelFor(key, spec)))

      if (spec.type === "array") {
        const nested = Array.isArray(item[key]) ? item[key] : []
        const nestedList = this.el("div", "space-y-1 pl-2 border-l border-slate-200 dark:border-slate-600")
        nested.forEach((nestedItem, nestedIndex) => {
          const row = this.el("div", "flex gap-1")
          Object.entries(spec.item || {}).forEach(([nestedKey]) => {
            const input = this.inputFor("string", nestedItem[nestedKey] || "")
            input.placeholder = nestedKey.replaceAll("_", " ")
            input.addEventListener("change", () => {
              nestedItem[nestedKey] = input.value
              this.sync()
            })
            row.append(input)
          })
          row.append(this.actionButton("×", () => {
            nested.splice(nestedIndex, 1)
            item[key] = nested
            this.syncAndRender()
          }, false, "text-red-500"))
          nestedList.append(row)
        })
        const addNested = this.actionButton(`+ Add ${key.replaceAll("_", " ")}`, () => {
          nested.push(this.blankItem(spec.item || {}))
          item[key] = nested
          this.syncAndRender()
        })
        nestedList.append(addNested)
        field.append(nestedList)
      } else {
        const input = this.inputFor(spec.type, item[key])
        input.addEventListener("change", () => {
          item[key] = input.type === "checkbox" ? input.checked : input.value
          this.sync()
        })
        field.append(input)
      }
      card.append(field)
    })

    return card
  }

  inputFor(type, value) {
    if (type === "boolean") {
      const input = document.createElement("input")
      input.type = "checkbox"
      input.className = "h-4 w-4 shrink-0 rounded-sm border-slate-300 bg-slate-50 text-teal-600 focus:ring-2 focus:ring-teal-500 dark:border-slate-600 dark:bg-slate-700 dark:ring-offset-slate-800 dark:focus:ring-teal-600 disabled:cursor-not-allowed disabled:opacity-50"
      input.checked = value === true || value === "true"
      return input
    }

    const input = type === "text" ? document.createElement("textarea") : document.createElement("input")
    if (input.tagName === "TEXTAREA") input.rows = 2
    else input.type = type === "number" ? "number" : type === "url" ? "url" : "text"
    input.value = value ?? ""
    input.className = "w-full min-w-0 rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
    return input
  }

  actionButton(label, action, disabled = false, extra = "") {
    const button = this.el("button", `rounded px-1.5 py-1 text-[10px] text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 ${extra}`, label)
    button.type = "button"
    button.disabled = disabled
    button.addEventListener("click", action)
    return button
  }

  move(index, delta) {
    const next = index + delta
    if (next < 0 || next >= this.items.length) return
    ;[this.items[index], this.items[next]] = [this.items[next], this.items[index]]
    this.syncAndRender()
  }

  remove(index) {
    this.items.splice(index, 1)
    this.syncAndRender()
  }

  syncAndRender() {
    this.sync()
    this.render()
  }

  sync() {
    this.inputTarget.value = JSON.stringify(this.items)
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  blankItem(schema) {
    return Object.fromEntries(Object.entries(schema).map(([key, spec]) => [key, spec.type === "array" ? [] : spec.type === "boolean" ? false : ""]))
  }

  labelFor(key, spec) {
    return spec.label || key.replaceAll("_", " ").replace(/^./, (char) => char.toUpperCase())
  }

  parse(value, fallback) {
    try { return JSON.parse(value) } catch (_error) { return fallback }
  }

  el(tag, className, text) {
    const element = document.createElement(tag)
    element.className = className
    if (text !== undefined) element.textContent = text
    return element
  }
}
